import React, { useRef, useState, useEffect } from 'react'
import styles from './MultiStepStepLayout.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Button } from 'blaze-ui/Button'
import ArrowLeft from 'blaze-ui/icons/20/ArrowLeft'
import ArrowRight from 'blaze-ui/icons/20/ArrowRight'
import ArrowSkip from 'blaze-ui/icons/20/ArrowSkip'
import cx from 'classnames'
import { Pill } from 'components/Pill/Pill'
import { Heading, HeadingLevel } from 'blaze-ui/Heading/Heading'
export interface MultiStepStepLayoutProps {
  pill?: string
  title?: string | React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
  onNext?: () => void
  onBack?: () => void
  onSkip?: () => void
  isNextButtonDisabled?: boolean
  nextButtonText?: string
  scrollContainerClassName?: string
  contentClassName?: string
  centerContent?: boolean
  centeredFooter?: boolean
  analyticsId?: string
  pillClassName?: string
  titleClassName?: string
  withArrowRight?: boolean
  withArrowLeft?: boolean
  mobileNextButtonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  headingLevel?: HeadingLevel
}

export const MultiStepStepLayout: React.FC<MultiStepStepLayoutProps> = ({
  title,
  description,
  children,
  isNextButtonDisabled,
  onNext,
  onBack,
  onSkip,
  nextButtonText = 'Continue',
  scrollContainerClassName,
  contentClassName,
  pill,
  centerContent = false,
  centeredFooter = false,
  withArrowRight = true,
  withArrowLeft = true,
  analyticsId,
  pillClassName,
  titleClassName,
  headingLevel = 1,
  mobileNextButtonSize = 'xl',
}) => {
  const hasFixedFooter = !!onNext
  const idSuffix = (analyticsId || pill || (typeof title === 'string' ? title : '') || 'step')
    .toLowerCase()
    .replace(/\s+/g, '-')

  const [showShadow, setShowShadow] = useState(false)
  const [isDesktopFooterSticky, setIsDesktopFooterSticky] = useState(false)
  const [footerStyle, setFooterStyle] = useState<React.CSSProperties>({})

  const spacerRef = useRef<HTMLDivElement>(null)
  const desktopFooterObserverRef = useRef<IntersectionObserver | null>(null)
  const desktopContentEndRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!hasFixedFooter) return

    const calculateFooterPosition = () => {
      // Find the parent container element (which is the main content area)
      const mainContentContainer = document.querySelector('#transitionContainer')
      if (mainContentContainer) {
        const rect = mainContentContainer.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2

        setFooterStyle({
          left: `calc(${centerX}px)`,
          transform: 'translateX(-50%)',
          width: mainContentContainer.clientWidth - 50,
        })
      }
    }

    // Mobile footer observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const isSpacerVisible = entries[0]?.isIntersecting
        setShowShadow(!isSpacerVisible)
      },
      { threshold: 0.2 },
    )

    // Desktop footer observer
    desktopFooterObserverRef.current = new IntersectionObserver(
      (entries) => {
        const isFooterVisible = entries[0]?.isIntersecting
        setIsDesktopFooterSticky(!isFooterVisible)

        // Calculate footer position when it becomes sticky
        if (!isFooterVisible) {
          calculateFooterPosition()
        } else {
          setFooterStyle({})
        }
      },
      { threshold: 0.1 },
    )

    // Start observing
    if (spacerRef.current) {
      observerRef.current.observe(spacerRef.current)
    }

    if (desktopContentEndRef.current) {
      desktopFooterObserverRef.current.observe(desktopContentEndRef.current)
    }

    // Also recalculate on resize to ensure correct positioning if window size changes
    window.addEventListener('resize', calculateFooterPosition)

    // Cleanup on unmount
    return () => {
      observerRef.current?.disconnect()
      desktopFooterObserverRef.current?.disconnect()
      window.removeEventListener('resize', calculateFooterPosition)
    }
  }, [hasFixedFooter])

  return (
    <div
      className={cx(`${styles.container} ${hasFixedFooter ? styles.hasFixedFooter : ''}`, {
        [styles.centerContent]: centerContent,
      })}
      ref={containerRef}
    >
      <div className={cx(styles.scrollContainer, scrollContainerClassName)}>
        {(title || description || pill) && (
          <div className={styles.header}>
            {pill && (
              <Pill size="lg" className={pillClassName}>
                {pill}
              </Pill>
            )}
            {(title || description) && (
              <section className={styles.titleAndDescription}>
                {title && (
                  <span className={titleClassName}>
                    <Heading level={headingLevel}>{title}</Heading>
                  </span>
                )}
                {description && <Text color="var(--dark-60)">{description}</Text>}
              </section>
            )}
          </div>
        )}
        <div className={cx(styles.content, contentClassName)}>{children}</div>

        {/* Observer element for desktop footer */}
        <div ref={desktopContentEndRef} className={styles.desktopFooterObserver} />

        {/* Spacer to ensure content doesn't get hidden behind fixed footer on mobile */}
        {/*<div ref={spacerRef} className={styles.footerSpacer} />*/}
      </div>

      {/* Desktop footer - conditionally sticky */}
      <ResponsiveDesign.Desktop>
        {(onNext || onBack || onSkip) && (
          <div
            className={cx(styles.desktopFooter, isDesktopFooterSticky && styles.stickyDesktopFooter, {
              [styles.centeredFooter]: centeredFooter,
            })}
            style={isDesktopFooterSticky ? footerStyle : undefined}
          >
            {onBack ? (
              <Button
                size="xl"
                variant="tertiary"
                frontIcon={withArrowLeft ? ArrowLeft : undefined}
                onPress={onBack}
                aria-label="Back"
                id={`back-${idSuffix}`}
              >
                Back
              </Button>
            ) : (
              <div />
            )}
            <div className={styles.skipAndNext}>
              {onSkip && (
                <Button
                  size="xl"
                  variant="ghost"
                  onPress={onSkip}
                  endIcon={ArrowSkip}
                  aria-label="Skip"
                  color="var(--dark-80)"
                  id={`skip-${idSuffix}`}
                >
                  Skip
                </Button>
              )}
              {onNext && (
                <Button
                  size="xl"
                  endIcon={withArrowRight ? ArrowRight : undefined}
                  isDisabled={isNextButtonDisabled}
                  onPress={() => onNext?.()}
                  aria-label={nextButtonText}
                  id={`next-${idSuffix}`}
                >
                  {nextButtonText}
                </Button>
              )}
            </div>
          </div>
        )}
      </ResponsiveDesign.Desktop>

      {/* Mobile footer - fixed to bottom when present */}
      <ResponsiveDesign.Mobile>
        {hasFixedFooter && (
          <div className={cx(styles.mobileFooter, showShadow && styles.withShadow)}>
            <div className={styles.mobileFooterContent}>
              {onNext && (
                <Button
                  size={mobileNextButtonSize}
                  endIcon={withArrowRight ? ArrowRight : undefined}
                  isDisabled={isNextButtonDisabled}
                  onPress={() => onNext?.()}
                  fullWidth
                  aria-label={nextButtonText}
                  id={`mobile-next-${idSuffix}`}
                >
                  {nextButtonText}
                </Button>
              )}
            </div>
          </div>
        )}
      </ResponsiveDesign.Mobile>
    </div>
  )
}
